import { useState } from "react";
import { isValidRelayUrl, LIMITS, parseInviteFragment } from "@psplit/ledger-core";
import { repo } from "../lib/repo.js";
import { navigate } from "../lib/hooks.js";
import { formatPaise, looksLikeVpa } from "../lib/money.js";
import { DEFAULT_RELAY_URL } from "../lib/config.js";
import { QrScan } from "../components/Qr.js";
import { NearbyJoinModal } from "../components/Nearby.js";
import { nearbyAvailable } from "../lib/nearbySession.js";

function inviteFromText(text: string): ReturnType<typeof parseInviteFragment> {
  const hashIndex = text.indexOf("#");
  const fragment = hashIndex >= 0 ? text.slice(hashIndex) : text;
  return parseInviteFragment(fragment);
}

export function Home() {
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState("");
  const [relayUrl, setRelayUrl] = useState(DEFAULT_RELAY_URL);
  const [joinText, setJoinText] = useState("");
  const [scanning, setScanning] = useState(false);
  const [nearbyJoin, setNearbyJoin] = useState(false);
  const [error, setError] = useState("");
  const [showAccount, setShowAccount] = useState(false);

  const me = repo.identity?.pubKey ?? "";
  const groups = repo.groups();

  const create = async () => {
    setError("");
    if (name.trim() === "") return setError("Group needs a name.");
    if (relayUrl !== "" && !isValidRelayUrl(relayUrl)) {
      return setError("Relay must be an https URL, or leave it empty for QR-only sync.");
    }
    const id = await repo.createGroup(name.trim(), relayUrl.trim());
    setCreating(false);
    setName("");
    navigate(`#/g/${id}`);
  };

  const join = async (text: string) => {
    setError("");
    const invite = inviteFromText(text);
    if (!invite) return setError("That does not look like a PaisaSplit invite.");
    await repo.joinGroup(invite);
    navigate(`#/g/${invite.groupId}`);
  };

  return (
    <div className="page">
      <header className="apphead">
        <h1 className="brand">PaisaSplit</h1>
        <button className="btn link whoami" onClick={() => setShowAccount((v) => !v)}>
          {repo.profileName} ⚙
        </button>
      </header>

      {showAccount && <AccountCard onClose={() => setShowAccount(false)} />}

      {repo.loadWarnings.length > 0 && (
        <div className="error">
          Some stored entries failed verification and were dropped. Sync with any group
          member to restore them.
        </div>
      )}

      <section>
        <h2>Your groups</h2>
        {groups.length === 0 && <p className="muted">No groups yet - create one or join a friend's.</p>}
        <div className="cards">
          {groups.map((g) => {
            const state = repo.state(g.groupId);
            const balance = state?.balances.get(me) ?? 0;
            const membership = repo.membership(g.groupId);
            return (
              <button key={g.groupId} className="card group" onClick={() => navigate(`#/g/${g.groupId}`)}>
                <div className="gname">{state?.name ?? g.name}</div>
                <div className="gsub">
                  {state
                    ? `${state.members.filter((m) => m.active).length} members · ${state.expenses.filter((e) => !e.voided).length} expenses`
                    : "waiting for first sync"}
                </div>
                {membership === "member" && state && (
                  <div className={balance >= 0 ? "chip ok" : "chip due"}>
                    {balance === 0
                      ? "settled up"
                      : balance > 0
                        ? `you are owed ${formatPaise(balance)}`
                        : `you owe ${formatPaise(-balance)}`}
                  </div>
                )}
                {membership === "pending" && <div className="chip">waiting to be admitted</div>}
                {membership === "left" && <div className="chip">you left this group</div>}
              </button>
            );
          })}
        </div>
      </section>

      <section>
        {creating ? (
          <div className="card">
            <h3>New group</h3>
            <label>
              Group name
              <input value={name} onChange={(e) => setName(e.target.value)} maxLength={LIMITS.name} placeholder="Goa trip, Flat 402…" />
            </label>
            <details className="adv">
              <summary>Advanced: sync relay</summary>
              <label>
                Relay URL (already set up for you - enables sync over the internet)
                <input
                  value={relayUrl}
                  onChange={(e) => setRelayUrl(e.target.value)}
                  placeholder="https://your-relay.workers.dev"
                />
              </label>
              <p className="fineprint">
                The relay only ever sees encrypted blobs. Clear it to sync face-to-face
                with QR codes only.
              </p>
            </details>
            <div className="row gap">
              <button className="btn primary" onClick={() => void create()}>
                Create group
              </button>
              <button className="btn" onClick={() => setCreating(false)}>
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <button className="btn primary wide" onClick={() => setCreating(true)}>
            + New group
          </button>
        )}
      </section>

      <section>
        <h2>Join a group</h2>
        <div className="card">
          <label>
            Paste an invite link
            <input value={joinText} onChange={(e) => setJoinText(e.target.value)} placeholder="https://…#ps=1&g=…" />
          </label>
          <div className="row gap">
            <button className="btn" onClick={() => void join(joinText)}>
              Join from link
            </button>
            <button className="btn" onClick={() => setScanning(true)}>
              Scan invite QR
            </button>
            {nearbyAvailable() && (
              <button className="btn" onClick={() => setNearbyJoin(true)}>
                Join a group nearby
              </button>
            )}
          </div>
          {nearbyAvailable() && (
            <p className="fineprint">
              Sitting with them? "Nearby" listens for a chirp from their phone, then asks them to
              let you in. No link to paste, no camera to aim.
            </p>
          )}
        </div>
      </section>

      {error !== "" && <div className="error">{error}</div>}
      {scanning && (
        <QrScan
          onPayload={(payload) => {
            setScanning(false);
            void join(payload);
          }}
          onClose={() => setScanning(false)}
        />
      )}
      {nearbyJoin && <NearbyJoinModal onClose={() => setNearbyJoin(false)} />}
    </div>
  );
}

// Account controls in one place, reachable from the home header: display name,
// UPI id (both announced to every group you are a member of) and the identity
// backup. The same profile card in each group's Settings stays for discovery.
function AccountCard({ onClose }: { onClose: () => void }) {
  const [profileName, setProfileName] = useState(repo.profileName);
  const [vpa, setVpa] = useState(repo.profileVpa);
  const [status, setStatus] = useState("");
  const [showIdentity, setShowIdentity] = useState(false);

  const save = async () => {
    if (profileName.trim() === "") return setStatus("Your name cannot be empty.");
    if (vpa !== "" && !looksLikeVpa(vpa)) {
      return setStatus("That UPI id does not look valid (name@bank).");
    }
    await repo.setProfile(profileName.trim(), vpa.trim());
    setStatus("Saved and announced to your groups.");
  };

  return (
    <div className="card">
      <h3>Your account</h3>
      <label>
        Name
        <input value={profileName} onChange={(e) => setProfileName(e.target.value)} maxLength={LIMITS.name} />
      </label>
      <label>
        UPI id (lets friends pay you in one tap)
        <input value={vpa} onChange={(e) => setVpa(e.target.value)} maxLength={LIMITS.vpa} placeholder="you@bank" />
      </label>
      <div className="row gap wrap">
        <button className="btn primary" onClick={() => void save()}>
          Save
        </button>
        <button className="btn" onClick={() => setShowIdentity((v) => !v)}>
          {showIdentity ? "Hide identity backup" : "Identity backup"}
        </button>
        <button className="btn" onClick={onClose}>
          Close
        </button>
      </div>
      {showIdentity && (
        <>
          <p className="error">
            This contains your PRIVATE key. Store it somewhere safe; anyone with it can
            write entries as you.
          </p>
          <textarea readOnly rows={5} value={repo.exportIdentity()} onFocus={(e) => e.target.select()} />
        </>
      )}
      {status !== "" && <div className="notice">{status}</div>}
    </div>
  );
}
